import {
  AgentsIcon,
  ActivityIcon,
  AlarmClockIcon as RoutineIcon,
  KnowledgeIcon,
  OrganizationIcon,
  UsersIcon,
  VaultIcon,
  ZapIcon as WorkflowIcon,
} from "lucide-react";
import { Link, useLocation } from "@tanstack/react-router";
import { useCallback } from "react";

import { useI18n, type MessageKey } from "~/i18n";
import { cn } from "~/lib/utils";
import { Badge } from "../ui/badge";
import { useActivitiesNeedsAttention } from "../activities/useActivitiesNeedsAttention";
import { useMorrowClerkAuth, useShowOrgWorkspaceNav } from "../clerk/morrowClerkAuth";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "../ui/sidebar";

const WORKSPACE_NAV_ITEMS = [
  { to: "/organization", labelKey: "sidebar.organization", icon: OrganizationIcon },
  { to: "/team", labelKey: "sidebar.team", icon: UsersIcon },
  { to: "/activities", labelKey: "sidebar.activities", icon: ActivityIcon },
  { to: "/agents", labelKey: "sidebar.agents", icon: AgentsIcon },
  { to: "/workflows", labelKey: "sidebar.workflows", icon: WorkflowIcon },
  { to: "/routines", labelKey: "sidebar.routines", icon: RoutineIcon },
  { to: "/vault", labelKey: "sidebar.vault", icon: VaultIcon },
  { to: "/knowledge", labelKey: "sidebar.knowledge", icon: KnowledgeIcon },
  // "Allowance" is hidden for now — restore by re-adding this entry (icon:
  // ShieldCheckIcon from lucide-react):
  // { to: "/allowance", labelKey: "sidebar.allowance", icon: ShieldCheckIcon },
  // "Messages" is hidden for now — restore by re-adding this entry (icon:
  // MessagesSquareIcon from lucide-react):
  // { to: "/messages", labelKey: "sidebar.messages", icon: MessagesSquareIcon },
] as const satisfies ReadonlyArray<{
  to:
    | "/team"
    | "/organization"
    | "/activities"
    | "/agents"
    | "/workflows"
    | "/routines"
    | "/knowledge"
    | "/vault";
  labelKey: MessageKey;
  icon: typeof UsersIcon;
}>;

export function SidebarWorkspaceNav() {
  const { t } = useI18n();
  const pathname = useLocation({ select: (location) => location.pathname });
  const { isMobile, setOpenMobile } = useSidebar();
  const { isLoaded: clerkLoaded, isSignedIn } = useMorrowClerkAuth();
  // "Team" and "Organization" are org-only: a personal (individual) account with
  // no active Clerk organization doesn't see them. Non-Clerk desktop/self-hosted
  // builds have no org concept, so they stay visible there (useShowOrgWorkspaceNav).
  const showOrg = useShowOrgWorkspaceNav();
  const navItems = showOrg
    ? WORKSPACE_NAV_ITEMS
    : WORKSPACE_NAV_ITEMS.filter((item) => item.to !== "/team" && item.to !== "/organization");
  const closeMobileSidebar = useCallback(() => {
    if (isMobile) setOpenMobile(false);
  }, [isMobile, setOpenMobile]);

  // Team / Agents / Messages are account features. Signed-out visitors have no
  // account to route into, so these are rendered inert (greyed, non-clickable)
  // rather than opening the sign-in modal — the greeting's sign-in CTA is the
  // single entry point while signed out.
  const disabled = clerkLoaded && !isSignedIn;
  const activitiesNeedsAttention = useActivitiesNeedsAttention();
  const handleNavClick = useCallback(() => {
    closeMobileSidebar();
  }, [closeMobileSidebar]);

  return (
    <SidebarMenu className="gap-px">
      {navItems.map((item) => {
        const Icon = item.icon;
        const showActivitiesAttention =
          item.to === "/activities" && !disabled && activitiesNeedsAttention;
        return (
          <SidebarMenuItem key={item.to}>
            <SidebarMenuButton
              aria-disabled={disabled || undefined}
              className={disabled ? "cursor-not-allowed opacity-45" : undefined}
              isActive={pathname === item.to}
              render={
                disabled ? <span aria-hidden /> : <Link to={item.to} onClick={handleNavClick} />
              }
            >
              <span className="relative inline-flex shrink-0">
                <Icon />
                {showActivitiesAttention ? (
                  <span
                    aria-hidden
                    className={cn(
                      "pointer-events-none absolute -right-0.5 -top-0.5 size-1.5 rounded-full bg-destructive",
                      "ring-2 ring-sidebar",
                    )}
                  />
                ) : null}
              </span>
              <span className="truncate">{t(item.labelKey)}</span>
              {item.to === "/agents" ? (
                <Badge size="sm" variant="warning" className="ml-auto shrink-0">
                  Beta
                </Badge>
              ) : null}
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}
